/**
 * Thin REST client for the customer-files backend primitive.
 * Returns parsed JSON on success, throws on non-2xx (except where redirect
 * semantics are useful).
 *
 * The page's main `request.js` pipeline wires `Authorization: Bearer <jwt>`
 * per request from authData. This module uses raw fetch (because it's
 * shared with the SW path's mental model) and needs the same Bearer header
 * injected via setAuthGetter — wired from stores/index.js at boot.
 *
 * Paths are program-scoped:
 *   /api/workspace/program/{program_id}/project/{project_id}/files/...
 */

let authGetter = () => null;
export function setAuthGetter(fn) { authGetter = fn; }

const BASE = (programId, projectId) =>
  `/api/workspace/program/${encodeURIComponent(programId)}/project/${encodeURIComponent(projectId)}/files`;

async function requestJSON(method, path, body) {
  const init = { method, headers: {} };
  const auth = authGetter();
  if (auth) init.headers["Authorization"] = auth;
  if (body !== undefined) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const resp = await fetch(path, init);
  if (!resp.ok && resp.status !== 204) {
    const text = await resp.text().catch(() => "");
    const err = new Error(`${method} ${path} ${resp.status}: ${text.slice(0, 200)}`);
    err.status = resp.status;
    throw err;
  }
  if (resp.status === 204) return undefined;
  return resp.json();
}

export function initUpload(programId, projectId, { name, content_type, size_bytes }) {
  return requestJSON("POST", `${BASE(programId, projectId)}/init-upload`, { name, content_type, size_bytes });
}

export function completeUpload(programId, projectId, fileId, body) {
  return requestJSON("POST", `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}/complete-upload`, body || {});
}

export function abortUpload(programId, projectId, fileId) {
  return requestJSON("POST", `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}/abort-upload`);
}

export function initVersion(programId, projectId, fileId, body) {
  return requestJSON("POST", `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}/versions`, body);
}

export async function listFiles(programId, projectId, { limit = 50, cursor, include = "ready", include_code = false } = {}) {
  const qs = new URLSearchParams();
  qs.set("limit", String(limit));
  if (cursor) qs.set("cursor", cursor);
  if (include) qs.set("include", include);
  if (include_code) qs.set("include_code", "true");
  return requestJSON("GET", `${BASE(programId, projectId)}?${qs.toString()}`);
}

export function getFileMeta(programId, projectId, fileId) {
  return requestJSON("GET", `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}`);
}

export function renameFile(programId, projectId, fileId, name) {
  return requestJSON("PATCH", `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}`, { name });
}

export function deleteFile(programId, projectId, fileId) {
  return requestJSON("DELETE", `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}`);
}

// Soft-archive (reversible) — distinct from the hard deleteFile above.
export function archiveFile(programId, projectId, fileId) {
  // POST (not DELETE): archiving is a reversible state transition, matching the
  // restore endpoint's POST (PLEX-4128 review).
  return requestJSON("POST", `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}/archive`);
}
export function restoreFile(programId, projectId, fileId) {
  return requestJSON("POST", `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}/restore`);
}

// ---- promote / demote / list-promoted (tier membership) ----
export function promoteFile(programId, projectId, fileId, { tier, dest_folder, source_chat_id } = {}) {
  // Only send optional fields when they have a value — the api.yaml requestBody
  // declares dest_folder/source_chat_id as `type: string`, so Connexion rejects
  // an explicit JSON `null` with a 400 ("None is not of type 'string'").
  const body = { tier };
  if (dest_folder != null) body.dest_folder = dest_folder;
  if (source_chat_id != null) body.source_chat_id = source_chat_id;
  return requestJSON(
    "POST",
    `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}/promote`,
    body,
  );
}

export function demoteFile(programId, projectId, fileId, tier) {
  return requestJSON(
    "DELETE",
    `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}/promote/${encodeURIComponent(tier)}`,
  );
}

export function getPromoteCapability(programId) {
  return requestJSON("GET", `/api/workspace/program/${encodeURIComponent(programId)}/promote-capability`);
}

export function listPromoted(tier, scopeId, { include = "ready" } = {}) {
  const qs = new URLSearchParams({ include });
  return requestJSON(
    "GET",
    `/api/workspace/promoted/${encodeURIComponent(tier)}/${encodeURIComponent(scopeId)}/files?${qs.toString()}`,
  );
}

// Fetch the file's content, following the content endpoint's 302 to the
// presigned S3 URL. A naive fetch(redirect:"manual") can't read the redirect
// Location in browsers (it returns an opaque redirect, status 0), so we let the
// browser follow the redirect transparently — the Authorization header is
// dropped on the cross-origin hop and S3 serves the presigned bytes (bucket
// CORS allows GET). Returns the Response; callers do .text() or .blob().
export async function fetchFileContent(programId, projectId, fileId, { versionId, rangeBytes } = {}) {
  const qs = versionId ? `?version_id=${encodeURIComponent(versionId)}` : "";
  const auth = authGetter();
  const headers = auth ? { Authorization: auth } : {};
  // Ranged read for preview: pull only the first rangeBytes so previewing a
  // 100MB+ file transfers a few KB, not the whole thing. S3 honors Range on the
  // presigned GET and returns 206 Partial Content (which is still resp.ok).
  if (rangeBytes != null) headers["Range"] = `bytes=0-${rangeBytes - 1}`;
  const resp = await fetch(
    `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}/content${qs}`,
    { method: "GET", headers },
  );
  if (!resp.ok) {
    const err = new Error(`content ${resp.status}`);
    err.status = resp.status;
    throw err;
  }
  return resp;
}

// Get a presigned attachment download URL (JSON). The frontend navigates to the
// returned S3 URL so the browser streams the file straight to disk with the
// right filename (Content-Disposition: attachment) — works for any size, no
// in-tab buffering. Use this for Download; use fetchFileContent for preview.
export async function getDownloadUrl(programId, projectId, fileId, versionId) {
  const qs = versionId ? `?version_id=${encodeURIComponent(versionId)}` : "";
  const r = await requestJSON("GET", `${BASE(programId, projectId)}/${encodeURIComponent(fileId)}/download-url${qs}`);
  return r.url;
}

// ---- chat-scoped folders + file membership (A3) ----
const CHAT_BASE = (chatId) => `/api/chat/${encodeURIComponent(chatId)}`;

export function listChatFolders(chatId, { include = "active" } = {}) {
  return requestJSON("GET", `${CHAT_BASE(chatId)}/folders?include=${encodeURIComponent(include)}`);
}
export function createChatFolder(chatId, name) {
  return requestJSON("POST", `${CHAT_BASE(chatId)}/folders`, { name });
}
export function renameChatFolder(chatId, folderName, newName) {
  return requestJSON("PATCH", `${CHAT_BASE(chatId)}/folders/${encodeURIComponent(folderName)}`, { new_name: newName });
}
export function archiveChatFolder(chatId, folderName) {
  return requestJSON("DELETE", `${CHAT_BASE(chatId)}/folders/${encodeURIComponent(folderName)}`);
}
export function restoreChatFolder(chatId, folderName) {
  return requestJSON("POST", `${CHAT_BASE(chatId)}/folders/${encodeURIComponent(folderName)}/restore`);
}
export function listChatFiles(chatId, { include = "ready", include_code = false } = {}) {
  const qs = new URLSearchParams({ include });
  if (include_code) qs.set("include_code", "true");
  return requestJSON("GET", `${CHAT_BASE(chatId)}/files?${qs.toString()}`);
}
export function assignChatFile(chatId, fileId, folderName) {
  // folderName null => Unfiled
  return requestJSON("PUT", `${CHAT_BASE(chatId)}/files/${encodeURIComponent(fileId)}`, { folder_name: folderName });
}
export function removeChatFile(chatId, fileId) {
  return requestJSON("DELETE", `${CHAT_BASE(chatId)}/files/${encodeURIComponent(fileId)}`);
}

// ---- scope (tier) folders ----
export function listScopeFolders(tier, scopeId, { includeArchived = false } = {}) {
  const qs = new URLSearchParams({ include_archived: String(includeArchived) });
  return requestJSON("GET", `/api/workspace/folders/${encodeURIComponent(tier)}/${encodeURIComponent(scopeId)}?${qs.toString()}`);
}
export function createScopeFolder(tier, scopeId, name) {
  return requestJSON("POST", `/api/workspace/folders/${encodeURIComponent(tier)}/${encodeURIComponent(scopeId)}`, { name });
}
